/**
 * The sign-in sheet.
 *
 * Two buttons and one line of reassurance. Providers are rendered from
 * `/api/me`, so an unconfigured provider is never offered — a button that
 * dead-ends costs more trust than a missing one.
 */
import { hintFor, orderProviders } from "../../shared/last-provider";
import { lastProvider } from "./device-memory";
import { providerMark } from "./provider-marks";
import { identity, refresh } from "./session";
import { type Provider, type SignInOptions, signIn } from "./sign-in";

const PROVIDER_LABEL: Record<Provider, string> = {
  discord: "Continue with Discord",
  google: "Continue with Google",
};

export interface SheetOptions extends SignInOptions {
  /** Why the sheet appeared. Shown as the heading. */
  reason?: string;
}

export async function openSignInSheet(opts: SheetOptions = {}): Promise<void> {
  // Providers may have changed since load; ask before drawing buttons.
  const me = identity().authConfigured ? identity() : await refresh();

  const configured = (Object.keys(PROVIDER_LABEL) as Provider[]).filter((p) => me.providers[p]);

  // Remembered provider first, and named. This is what prevents the
  // duplicate-account failure — signing up with Google, returning later, tapping
  // Discord, and landing in an empty account — rather than cleaning up after it.
  const remembered = lastProvider();
  const available = orderProviders(configured, remembered);
  const hint = hintFor(remembered, configured);

  const dialog = document.createElement("dialog");
  dialog.className = "signin-sheet";
  /*
   * The close control stays inside a `method="dialog"` form: that closes the
   * dialog natively, with no listener, so it still works if this module's
   * JavaScript has failed. CSS lifts it to the top-right corner — it was
   * rendering as the first thing in the flow, an unstyled browser button above
   * the heading, which is what made the sheet look unfinished.
   */
  dialog.innerHTML = `
    <form method="dialog" class="signin-sheet__close">
      <button value="cancel" aria-label="Close">
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false"
             fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <path d="M6 6l12 12M18 6L6 18"/>
        </svg>
      </button>
    </form>
    <h2 class="signin-sheet__title">${escapeHtml(opts.reason ?? "Save your work")}</h2>
    <p class="signin-sheet__lede">Your designs follow you to any device, and stay yours.</p>
    ${hint ? `<p class="signin-sheet__hint">${escapeHtml(hint)}</p>` : ""}
    <div class="signin-sheet__providers"></div>
    <p class="signin-sheet__note">No password. We never post anything on your behalf.</p>
    <p class="signin-sheet__error" hidden></p>
  `;

  const list = dialog.querySelector(".signin-sheet__providers") as HTMLDivElement;
  const error = dialog.querySelector(".signin-sheet__error") as HTMLParagraphElement;

  if (available.length === 0) {
    // Better to say so than to render a button that cannot work.
    error.hidden = false;
    error.textContent =
      "Sign-in isn't set up yet. Your work is saved on this device in the meantime.";
  }

  for (const provider of available) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = `signin-sheet__provider signin-sheet__provider--${provider}`;
    // The shell decides what "remembered" looks like; we only mark it.
    if (provider === remembered) button.dataset.remembered = "true";
    /*
     * Mark then label. innerHTML is safe here — both halves are ours: the SVG
     * is a module constant and the label comes from PROVIDER_LABEL, keyed by a
     * union type. No user input reaches this string.
     */
    button.innerHTML = `${providerMark(provider)}<span>${PROVIDER_LABEL[provider]}</span>`;
    button.addEventListener("click", async () => {
      for (const b of list.querySelectorAll("button")) b.disabled = true;
      error.hidden = true;
      try {
        await signIn(provider, opts);
      } catch (err) {
        // Most likely the flush failed, which means unsaved work would be lost
        // by redirecting. Staying put is the correct outcome.
        error.hidden = false;
        error.textContent = (err as Error).message;
        for (const b of list.querySelectorAll("button")) b.disabled = false;
      }
    });
    list.appendChild(button);
  }

  /*
   * Clicking the backdrop closes it. <dialog> does not do this natively, and on
   * a phone the area outside a sheet is the first thing people tap to dismiss.
   *
   * The target test is the dialog itself: a click that lands on the element
   * rather than on any child IS the backdrop, because the padding box is fully
   * covered by content. Comparing coordinates to getBoundingClientRect would
   * misfire on the rounded corners.
   */
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dialog.close("backdrop");
  });

  document.body.appendChild(dialog);
  dialog.showModal();

  await new Promise<void>((resolve) => {
    dialog.addEventListener("close", () => {
      dialog.remove();
      resolve();
    });
  });
}

function escapeHtml(value: string): string {
  const div = document.createElement("div");
  div.textContent = value;
  return div.innerHTML;
}
